"use client"

import { FormEventHandler, useCallback, useState } from "react"
import { captureException } from "@sentry/nextjs"
import { useRouter } from "next/navigation"

import API from "@/utils/api"

import { Body1, Display8 } from "@/text"
import FormField from "@/components/FormField"
import Button from "@/components/Button"
import ErrorWithData from "@/utils/ErrorWithData"
import useToast from "@/hooks/useToast"
import { useLoginTab } from "../../context"

export default function ForgotPassword() {
  const { setLabel } = useLoginTab()
  const { addToast } = useToast()
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  setLabel("Forgot Password")

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault()
      router.prefetch("/password/submitted")
      setIsSubmitting(true)
      try {
        const form = new FormData(e.currentTarget)
        const { data, response } = await API.post(
          "/rest-auth/password/reset/",
          form
        )
        if (!response.ok) {
          throw new ErrorWithData("Error requesting password reset", data)
        }
        router.push(
          `/password/submitted?${new URLSearchParams(
            Array.from(form.entries()) as [string, string][]
          )}`
        )
      } catch (err: any) {
        captureException(err)
        addToast(err, "error")
      }
      setIsSubmitting(false)
    },
    [addToast, router]
  )

  return (
    <>
      <Display8 className="mb-3">Password Reset</Display8>
      <Body1 className="mb-5">
        Enter your the email address that you used to register. We&apos;ll send
        you an email with a link to reset your password.
      </Body1>
      <form onSubmit={onSubmit}>
        <FormField
          name="email"
          label="Email"
          required
          autoFocus
          className="mb-6"
        />
        <Button
          as="button"
          type="submit"
          theme="black"
          className="w-full"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Send Reset Link"}
        </Button>
      </form>
    </>
  )
}
